/**
 * ColorBrewer palettes for data visualization.
 * Based on Cynthia Brewer's ColorBrewer 2.0: https://colorbrewer2.org/
 */

export type PaletteCategory = 'sequential' | 'diverging' | 'qualitative';

/** Color arrays indexed by numeric-string size key (e.g. "3", "5", "9"). */
type SizeMap = Record<string, readonly string[]>;

/** All named palettes within a category. */
type CategoryMap = Record<string, SizeMap>;

/** Top-level structure: category → name → size → colors. */
type PaletteData = Record<PaletteCategory, CategoryMap>;

export const ColorBrewerPalettes: PaletteData = {
  sequential: {
    Blues: {
      3: ['#deebf7', '#9ecae1', '#3182bd'],
      4: ['#eff3ff', '#bdd7e7', '#6baed6', '#2171b5'],
      5: ['#eff3ff', '#bdd7e7', '#6baed6', '#3182bd', '#08519c'],
      6: ['#eff3ff', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c'],
      7: ['#eff3ff', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#084594'],
      8: ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#084594'],
      9: ['#f7fbff', '#deebf7', '#c6dbef', '#9ecae1', '#6baed6', '#4292c6', '#2171b5', '#08519c', '#08306b'],
    },
    Greens: {
      3: ['#e5f5e0', '#a1d99b', '#31a354'],
      4: ['#edf8e9', '#bae4b3', '#74c476', '#238b45'],
      5: ['#edf8e9', '#bae4b3', '#74c476', '#31a354', '#006d2c'],
      6: ['#edf8e9', '#c7e9c0', '#a1d99b', '#74c476', '#31a354', '#006d2c'],
      7: ['#edf8e9', '#c7e9c0', '#a1d99b', '#74c476', '#41ab5d', '#238b45', '#005a32'],
      8: ['#f7fcf5', '#e5f5e0', '#c7e9c0', '#a1d99b', '#74c476', '#41ab5d', '#238b45', '#005a32'],
      9: ['#f7fcf5', '#e5f5e0', '#c7e9c0', '#a1d99b', '#74c476', '#41ab5d', '#238b45', '#006d2c', '#00441b'],
    },
    Reds: {
      3: ['#fee0d2', '#fc9272', '#de2d26'],
      4: ['#fee5d9', '#fcae91', '#fb6a4a', '#cb181d'],
      5: ['#fee5d9', '#fcae91', '#fb6a4a', '#de2d26', '#a50f15'],
      6: ['#fee5d9', '#fcbba1', '#fc9272', '#fb6a4a', '#de2d26', '#a50f15'],
      7: ['#fee5d9', '#fcbba1', '#fc9272', '#fb6a4a', '#ef3b2c', '#cb181d', '#99000d'],
      8: ['#fff5f0', '#fee0d2', '#fcbba1', '#fc9272', '#fb6a4a', '#ef3b2c', '#cb181d', '#99000d'],
      9: ['#fff5f0', '#fee0d2', '#fcbba1', '#fc9272', '#fb6a4a', '#ef3b2c', '#cb181d', '#a50f15', '#67000d'],
    },
    Oranges: {
      3: ['#fee6ce', '#fdae6b', '#e6550d'],
      4: ['#feedde', '#fdbe85', '#fd8d3c', '#d94701'],
      5: ['#feedde', '#fdbe85', '#fd8d3c', '#e6550d', '#a63603'],
      6: ['#feedde', '#fdd0a2', '#fdae6b', '#fd8d3c', '#e6550d', '#a63603'],
      7: ['#feedde', '#fdd0a2', '#fdae6b', '#fd8d3c', '#f16913', '#d94801', '#8c2d04'],
      8: ['#fff5eb', '#fee6ce', '#fdd0a2', '#fdae6b', '#fd8d3c', '#f16913', '#d94801', '#8c2d04'],
      9: ['#fff5eb', '#fee6ce', '#fdd0a2', '#fdae6b', '#fd8d3c', '#f16913', '#d94801', '#a63603', '#7f2704'],
    },
    Purples: {
      3: ['#efedf5', '#bcbddc', '#756bb1'],
      4: ['#f2f0f7', '#cbc9e2', '#9e9ac8', '#6a51a3'],
      5: ['#f2f0f7', '#cbc9e2', '#9e9ac8', '#756bb1', '#54278f'],
      6: ['#f2f0f7', '#dadaeb', '#bcbddc', '#9e9ac8', '#756bb1', '#54278f'],
      7: ['#f2f0f7', '#dadaeb', '#bcbddc', '#9e9ac8', '#807dba', '#6a51a3', '#4a1486'],
      8: ['#fcfbfd', '#efedf5', '#dadaeb', '#bcbddc', '#9e9ac8', '#807dba', '#6a51a3', '#4a1486'],
      9: ['#fcfbfd', '#efedf5', '#dadaeb', '#bcbddc', '#9e9ac8', '#807dba', '#6a51a3', '#54278f', '#3f007d'],
    },
  },

  diverging: {
    RdYlBu: {
      3: ['#fc8d59', '#ffffbf', '#91bfdb'],
      4: ['#d7191c', '#fdae61', '#abd9e9', '#2c7bb6'],
      5: ['#d7191c', '#fdae61', '#ffffbf', '#abd9e9', '#2c7bb6'],
      6: ['#d73027', '#fc8d59', '#fee090', '#e0f3f8', '#91bfdb', '#4575b4'],
      7: ['#d73027', '#fc8d59', '#fee090', '#ffffbf', '#e0f3f8', '#91bfdb', '#4575b4'],
      8: ['#d73027', '#f46d43', '#fdae61', '#fee090', '#e0f3f8', '#abd9e9', '#74add1', '#4575b4'],
      9: ['#d73027', '#f46d43', '#fdae61', '#fee090', '#ffffbf', '#e0f3f8', '#abd9e9', '#74add1', '#4575b4'],
      10: ['#a50026', '#d73027', '#f46d43', '#fdae61', '#fee090', '#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695'],
      11: ['#a50026', '#d73027', '#f46d43', '#fdae61', '#fee090', '#ffffbf', '#e0f3f8', '#abd9e9', '#74add1', '#4575b4', '#313695'],
    },
    RdYlGn: {
      3: ['#fc8d59', '#ffffbf', '#91cf60'],
      4: ['#d7191c', '#fdae61', '#a6d96a', '#1a9641'],
      5: ['#d7191c', '#fdae61', '#ffffbf', '#a6d96a', '#1a9641'],
      6: ['#d73027', '#fc8d59', '#fee08b', '#d9ef8b', '#91cf60', '#1a9850'],
      7: ['#d73027', '#fc8d59', '#fee08b', '#ffffbf', '#d9ef8b', '#91cf60', '#1a9850'],
      8: ['#d73027', '#f46d43', '#fdae61', '#fee08b', '#d9ef8b', '#a6d96a', '#66bd63', '#1a9850'],
      9: ['#d73027', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#d9ef8b', '#a6d96a', '#66bd63', '#1a9850'],
      10: ['#a50026', '#d73027', '#f46d43', '#fdae61', '#fee08b', '#d9ef8b', '#a6d96a', '#66bd63', '#1a9850', '#006837'],
      11: ['#a50026', '#d73027', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#d9ef8b', '#a6d96a', '#66bd63', '#1a9850', '#006837'],
    },
    Spectral: {
      3: ['#fc8d59', '#ffffbf', '#99d594'],
      4: ['#d7191c', '#fdae61', '#abdda4', '#2b83ba'],
      5: ['#d7191c', '#fdae61', '#ffffbf', '#abdda4', '#2b83ba'],
      6: ['#d53e4f', '#fc8d59', '#fee08b', '#e6f598', '#99d594', '#3288bd'],
      7: ['#d53e4f', '#fc8d59', '#fee08b', '#ffffbf', '#e6f598', '#99d594', '#3288bd'],
      8: ['#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#e6f598', '#abdda4', '#66c2a5', '#3288bd'],
      9: ['#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#e6f598', '#abdda4', '#66c2a5', '#3288bd'],
      10: ['#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'],
      11: ['#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'],
    },
    PiYG: {
      3: ['#e9a3c9', '#f7f7f7', '#a1d76a'],
      4: ['#d01c8b', '#f1b6da', '#b8e186', '#4dac26'],
      5: ['#d01c8b', '#f1b6da', '#f7f7f7', '#b8e186', '#4dac26'],
      6: ['#c51b7d', '#e9a3c9', '#fde0ef', '#e6f5d0', '#a1d76a', '#4d9221'],
      7: ['#c51b7d', '#e9a3c9', '#fde0ef', '#f7f7f7', '#e6f5d0', '#a1d76a', '#4d9221'],
      8: ['#c51b7d', '#de77ae', '#f1b6da', '#fde0ef', '#e6f5d0', '#b8e186', '#7fbc41', '#4d9221'],
      9: ['#c51b7d', '#de77ae', '#f1b6da', '#fde0ef', '#f7f7f7', '#e6f5d0', '#b8e186', '#7fbc41', '#4d9221'],
      10: ['#8e0152', '#c51b7d', '#de77ae', '#f1b6da', '#fde0ef', '#e6f5d0', '#b8e186', '#7fbc41', '#4d9221', '#276419'],
      11: ['#8e0152', '#c51b7d', '#de77ae', '#f1b6da', '#fde0ef', '#f7f7f7', '#e6f5d0', '#b8e186', '#7fbc41', '#4d9221', '#276419'],
    },
  },

  qualitative: {
    Set1: {
      3: ['#e41a1c', '#377eb8', '#4daf4a'],
      4: ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3'],
      5: ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00'],
      6: ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00', '#ffff33'],
      7: ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00', '#ffff33', '#a65628'],
      8: ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00', '#ffff33', '#a65628', '#f781bf'],
      9: ['#e41a1c', '#377eb8', '#4daf4a', '#984ea3', '#ff7f00', '#ffff33', '#a65628', '#f781bf', '#999999'],
    },
    Set2: {
      3: ['#66c2a5', '#fc8d62', '#8da0cb'],
      4: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3'],
      5: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3', '#a6d854'],
      6: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3', '#a6d854', '#ffd92f'],
      7: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3', '#a6d854', '#ffd92f', '#e5c494'],
      8: ['#66c2a5', '#fc8d62', '#8da0cb', '#e78ac3', '#a6d854', '#ffd92f', '#e5c494', '#b3b3b3'],
    },
    Set3: {
      3: ['#8dd3c7', '#ffffb3', '#bebada'],
      4: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072'],
      5: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3'],
      6: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462'],
      7: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69'],
      8: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69', '#fccde5'],
      9: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69', '#fccde5', '#d9d9d9'],
      10: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69', '#fccde5', '#d9d9d9', '#bc80bd'],
      11: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69', '#fccde5', '#d9d9d9', '#bc80bd', '#ccebc5'],
      12: ['#8dd3c7', '#ffffb3', '#bebada', '#fb8072', '#80b1d3', '#fdb462', '#b3de69', '#fccde5', '#d9d9d9', '#bc80bd', '#ccebc5', '#ffed6f'],
    },
    Paired: {
      3: ['#a6cee3', '#1f78b4', '#b2df8a'],
      4: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c'],
      5: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99'],
      6: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c'],
      7: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f'],
      8: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00'],
      9: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6'],
      10: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a'],
      11: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a', '#ffff99'],
      12: ['#a6cee3', '#1f78b4', '#b2df8a', '#33a02c', '#fb9a99', '#e31a1c', '#fdbf6f', '#ff7f00', '#cab2d6', '#6a3d9a', '#ffff99', '#b15928'],
    },
    Dark2: {
      3: ['#1b9e77', '#d95f02', '#7570b3'],
      4: ['#1b9e77', '#d95f02', '#7570b3', '#e7298a'],
      5: ['#1b9e77', '#d95f02', '#7570b3', '#e7298a', '#66a61e'],
      6: ['#1b9e77', '#d95f02', '#7570b3', '#e7298a', '#66a61e', '#e6ab02'],
      7: ['#1b9e77', '#d95f02', '#7570b3', '#e7298a', '#66a61e', '#e6ab02', '#a6761d'],
      8: ['#1b9e77', '#d95f02', '#7570b3', '#e7298a', '#66a61e', '#e6ab02', '#a6761d', '#666666'],
    },
    Accent: {
      3: ['#7fc97f', '#beaed4', '#fdc086'],
      4: ['#7fc97f', '#beaed4', '#fdc086', '#ffff99'],
      5: ['#7fc97f', '#beaed4', '#fdc086', '#ffff99', '#386cb0'],
      6: ['#7fc97f', '#beaed4', '#fdc086', '#ffff99', '#386cb0', '#f0027f'],
      7: ['#7fc97f', '#beaed4', '#fdc086', '#ffff99', '#386cb0', '#f0027f', '#bf5b17'],
      8: ['#7fc97f', '#beaed4', '#fdc086', '#ffff99', '#386cb0', '#f0027f', '#bf5b17', '#666666'],
    },
    Pastel1: {
      3: ['#fbb4ae', '#b3cde3', '#ccebc5'],
      4: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4'],
      5: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4', '#fed9a6'],
      6: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4', '#fed9a6', '#ffffcc'],
      7: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4', '#fed9a6', '#ffffcc', '#e5d8bd'],
      8: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4', '#fed9a6', '#ffffcc', '#e5d8bd', '#fddaec'],
      9: ['#fbb4ae', '#b3cde3', '#ccebc5', '#decbe4', '#fed9a6', '#ffffcc', '#e5d8bd', '#fddaec', '#f2f2f2'],
    },
    Tableau10: {
      10: ['#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F', '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC'],
    },
    TableauAutomatic20: {
      20: ['#4E79A7', '#A0CBE8', '#F28E2B', '#FFBE7D', '#59A14F', '#8CD17D', '#E15759', '#FF9D9A', '#B07AA1', '#D4A6C8', '#9D7660', '#D7B5A6', '#D37295', '#FABFD2', '#79706E', '#BAB0AC', '#B6992D', '#F1CE63', '#499894', '#86BCB6'],
    },
    TableauColorBlind: {
      10: ['#326492', '#EE8E31', '#A8AFAE', '#60686F', '#6CA2C1', '#BD6223', '#7E8388', '#A2C3D8', '#EEC185', '#C2C6CA'],
    },
    TableauSeattleGrays: {
      5: ['#707B87', '#B1B5B7', '#5E6063', '#D6D6D4', '#94999D'],
    },
    TableauTrafficLight: {
      9: ['#A82626', '#D49A3D', '#52834E', '#D65C48', '#D6BD56', '#6CB17E', '#EB7A63', '#F4D785', '#98C895'],
    },
    TableauGreenOrangeTeal: {
      12: ['#558C56', '#9CD491', '#E28E37', '#F5C778', '#5D9BA6', '#A3D4E0', '#959842', '#CDD25E', '#927D33', '#F1D857', '#417876', '#93BCA8'],
    },
    TableauJewelBright: {
      9: ['#CB3838', '#EB824E', '#D9A544', '#F3D76E', '#71AC75', '#7BCBC8', '#9FD5E6', '#9E9CC7', '#BBC2DE'],
    },
    TableauNurielStone: {
      9: ['#817597', '#85B292', '#4F919C', '#D0B553', '#9FA0C3', '#94C8BC', '#959CA1', '#40768C', '#968D3F'],
    },
    TableauMillerStone: {
      11: ['#596A84', '#8FA3B0', '#9CC5A1', '#7D936B', '#BDB969', '#E08F58', '#EBB063', '#A36C61', '#D4D3A7', '#B8ADA0', '#736C63'],
    },
    TableauSuperfishelStone: {
      10: ['#63799B', '#EBAF54', '#D0766C', '#98BDBA', '#6DA57F', '#B6BD61', '#AE7D8C', '#AA968D', '#ABB3AF', '#797978'],
    },
    TableauSummer: {
      8: ['#92AD44', '#C2D075', '#BD5056', '#E0858C', '#5299A2', '#9BCBCB', '#E5A056', '#ECC086'],
    },
    TableauWinter: {
      10: ['#827187', '#BBA9B1', '#9FA25D', '#D0D08A', '#C76662', '#E89C89', '#727271', '#B7B2AD', '#A68C83', '#D4B8AF'],
    },
    TableauLightningWater: {
      6: ['#9CE4E0', '#86C5E3', '#5993C9', '#4575B4', '#2C4F89', '#1B2A57'],
    },
    TableauLightningSunrise: {
      6: ['#EED99F', '#E6BF72', '#D9974B', '#BE7D45', '#643E48', '#341E55'],
    },
    TableauLightningColorSafe: {
      6: ['#6F9ACE', '#CFA7BC', '#558A6B', '#E19E45', '#2D5F82', '#EBE166'],
    },
    TableauLightningDefault: {
      6: ['#78B4CF', '#CD7550', '#E4AF5C', '#7EA278', '#72C1C2', '#CE8EA5'],
    },
    TableauLightningLight: {
      6: ['#5F96D6', '#92BDE7', '#975CC5', '#C097D3', '#559A94', '#76C8BF'],
    },
    TableauLightningDark: {
      6: ['#3B76BD', '#659CD6', '#7336C6', '#A26CC5', '#45756C', '#5CA19C'],
    },
    TableauLightningBluegrass: {
      6: ['#CDEFAB', '#A3D9A4', '#71BEAA', '#609DA1', '#3E6B7C', '#234366'],
    },
    TableauRedGold: {
      20: ['#EACE7F', '#E9C46D', '#DFB362', '#DFA85E', '#D1995C', '#DF9E67', '#E19961', '#E09361', '#DC8E5E', '#DD8756', '#C56C51', '#CD6952', '#CE634D', '#C75E4B', '#C55546', '#BA4644', '#B63F3F', '#B43B3D', '#A8343C', '#9E2B36'],
    },
    TableauGreenGold: {
      20: ['#E9CF7E', '#E6CA74', '#CEC06F', '#C4B66B', '#ACAC68', '#A9C174', '#A2BD6E', '#97B367', '#8CA962', '#829F5D', '#689458', '#659254', '#5F8D51', '#59874D', '#53824A', '#467246', '#426E42', '#3F693F', '#3C643C', '#386038'],
    },
    TableauHueCircle: {
      20: ['#5B96B7', '#6BA9A9', '#6DA68F', '#68A578', '#5F9464', '#7AA85B', '#ACBC56', '#D3C25D', '#EBBC59', '#E69C4E', '#D0713E', '#CC4E3E', '#D24F53', '#D76161', '#DF7681', '#D373A5', '#BF6DA8', '#9D6AB1', '#756BB1', '#526B9D'],
    },
  },
};

/**
 * Get a palette by category, name, and minimum size.
 * Returns the smallest available size >= requested size, or the largest if none is big enough.
 * Returns undefined when the category or name is not found.
 */
export function getPalette(
  category: PaletteCategory,
  name: string,
  size: number,
): readonly string[] | undefined {
  const palette = ColorBrewerPalettes[category]?.[name];
  if (!palette) return undefined;

  const availableSizes = Object.keys(palette)
    .map(Number)
    .sort((a, b) => a - b);

  const selectedSize =
    availableSizes.find((s) => s >= size) ?? availableSizes[availableSizes.length - 1];

  if (selectedSize === undefined) return undefined;

  return palette[String(selectedSize)];
}

export interface PaletteInfo {
  category: string;
  name: string;
  displayName: string;
  maxSize: number;
}

/** Return every palette as a flat list of descriptor objects. */
export function getAllPalettes(): PaletteInfo[] {
  const palettes: PaletteInfo[] = [];

  for (const [category, categoryPalettes] of Object.entries(ColorBrewerPalettes)) {
    for (const [name, sizes] of Object.entries(categoryPalettes)) {
      palettes.push({
        category,
        name,
        displayName: name,
        maxSize: Math.max(...Object.keys(sizes).map(Number)),
      });
    }
  }

  return palettes;
}

/**
 * Get exactly `count` colors from a palette, cycling through the palette colors if count exceeds
 * the palette size.
 * Returns undefined when the category or name is not found.
 */
export function getColorsFromPalette(
  category: PaletteCategory,
  name: string,
  count: number,
): string[] | undefined {
  const palette = getPalette(category, name, count);
  if (!palette) return undefined;

  if (count <= palette.length) {
    return palette.slice(0, count);
  }

  const colors: string[] = [];
  let index = 0;
  while (colors.length < count) {
    const color = palette[index % palette.length];
    if (color !== undefined) {
      colors.push(color);
    }
    index++;
  }
  return colors;
}
